const { getStayType } = require('./stayTypeService');

const getAddressComponent = (place, type, key = 'long_name') => {
    const address_components = Array.isArray(place?.address_components) ? place.address_components : [];
    const component = address_components.find((item) => Array.isArray(item?.types) && item.types.includes(type));
    return component?.[key];
};

const getCityFromPlace = (place) => {
    return (
        place?.city ||
        getAddressComponent(place, 'locality') ||
        getAddressComponent(place, 'postal_town') ||
        getAddressComponent(place, 'administrative_area_level_2') ||
        getAddressComponent(place, 'administrative_area_level_1')
    );
};

const getCountryFromPlace = (place) => {
    return place?.country || getAddressComponent(place, 'country');
};

const getCountryCodeFromPlace = (place) => {
    return place?.country_code || place?.countryCode || getAddressComponent(place, 'country', 'short_name');
};

// The total and its currency must come from the same object, or amounts get mislabeled.
const getTotalPriceWithCurrency = (sources) => {
    const source = sources.find((candidate) => candidate?.total_price || candidate?.totalPrice);
    return { total_price: source?.total_price || source?.totalPrice, total_price_currency: source?.currency };
};

const getDestinationObject = (destination, hotel) => {
    if (!destination && !hotel) {
        return;
    }

    if (destination) {
        let { place } = destination;

        if (!place) {
            // if no place object and hotel id on destination root
            // try to copy the root
            if (destination._id || destination.id || destination.hotel_id || destination.place_id) {
                place = destination;
            } else {
                console.error('getDestinationObject::: no place provided in destination');
                return;
            }
        }
        const hotel_context = hotel || place.hotel || place.hotel_data;
        const price_per_night =
            place.price_per_night ||
            place.pricePerNight ||
            place.representativeDeal?.pricePerNight ||
            place.representative_deals?.cheapest?.pricePerNight ||
            hotel_context?.representativeDeal?.pricePerNight;
        const { total_price, total_price_currency } = getTotalPriceWithCurrency([
            place,
            place.representativeDeal,
            place.representative_deals?.cheapest,
            hotel_context?.representativeDeal,
        ]);
        const currency =
            place.currency ||
            place.representativeDeal?.currency ||
            place.representative_deals?.cheapest?.currency ||
            hotel_context?.representativeDeal?.currency ||
            hotel_context?.currency;
        const hotel_id = hotel_context?.id || hotel_context?._id;
        const hotel_name = hotel_context?.name || hotel_context?.title || place.hotel_name;
        const address = hotel_context?.address || place.address || place.formatted_address;
        const country = hotel_context?.country || getCountryFromPlace(place);
        const country_code = hotel_context?.country_code || hotel_context?.countryCode || getCountryCodeFromPlace(place);
        const city = hotel_context?.city || getCityFromPlace(place);
        const stars = hotel_context?.stars || place.stars;
        const zip = hotel_context?.zip || place.zip;
        const place_id = place.place_id || hotel_context?.place_id;
        const hotel_stay_type = getStayType(hotel_context?.category_flags || place.category_flags);

        return {
            ...(place._id && { hotel_id: place._id }),
            ...(place.id && { hotel_id: place.id }),
            ...(place.hotel_id && { hotel_id: place.hotel_id }),
            ...(hotel_id && { hotel_id }),
            ...(place_id && { place_id }),
            ...(hotel_name && { hotel_name }),
            ...(place.title && { name: place.title }),
            ...(place.name && { name: place.name }),
            ...(!place.title && !place.name && hotel_name && { name: hotel_name }),
            ...(address && { address }),
            ...(country && { country }),
            ...(country_code && { country_code }),
            ...(city && { city }),
            ...(stars && { stars }),
            ...(place.item_varient && { item_varient: place.item_varient }),
            ...(place.stay_type && { stay_type: place.stay_type }),
            ...(!place.stay_type && !place.item_varient && hotel_id && { stay_type: 'Hotel' }),
            ...(hotel_stay_type && { hotel_stay_type }),
            ...(place.autocomplete_category && { autocomplete_category: place.autocomplete_category }),
            ...(price_per_night && { price_per_night }),
            ...(total_price && { total_price }),
            ...(currency && { currency }),
            ...(total_price_currency && { total_price_currency }),
            ...(zip && { zip }),
            ...(place.category && { autocomplete_category: place.category }), // mobile
            ...(destination.autocomplete_category && { autocomplete_category: destination.autocomplete_category }), // desktop
            ...(destination.autocomplete_index && { autocomplete_index: destination.autocomplete_index }) // desktop
        };
    } else {
        const price_per_night =
            hotel.price_per_night ||
            hotel.pricePerNight ||
            hotel.representativeDeal?.pricePerNight ||
            hotel.representative_deals?.cheapest?.pricePerNight;
        const { total_price, total_price_currency } = getTotalPriceWithCurrency([
            hotel,
            hotel.representativeDeal,
            hotel.representative_deals?.cheapest,
        ]);
        const currency =
            hotel.currency ||
            hotel.representativeDeal?.currency ||
            hotel.representative_deals?.cheapest?.currency;
        const hotel_stay_type = getStayType(hotel.category_flags);
        return {
            hotel_id: hotel.id,
            name: hotel.name,
            address: hotel.address,
            country: hotel.country,
            country_code: hotel.country_code,
            city: hotel.city,
            stay_type: hotel.stay_type || 'Hotel',
            ...(hotel_stay_type && { hotel_stay_type }),
            ...(hotel.stars && { stars: hotel.stars }),
            ...(price_per_night && { price_per_night }),
            ...(total_price && { total_price }),
            ...(currency && { currency }),
            ...(total_price_currency && { total_price_currency }),
            zip: hotel.zip
        };
    }
};

// A booking from the hotel page describes the hotel, not the searched place the user came from.
const withHotelContext = (destination, hotel) => {
    if (!hotel) {
        return destination;
    }

    const place = destination?.place || destination || {};
    return {
        ...destination,
        place: {
            ...place,
            ...(hotel.id && { hotel_id: hotel.id }),
            ...(hotel.name && { hotel_name: hotel.name }),
            ...(hotel.address && { address: hotel.address }),
            ...(hotel.city && { city: hotel.city }),
            ...(hotel.country && { country: hotel.country }),
            ...(hotel.country_code && { country_code: hotel.country_code }),
            ...(hotel.stars && { stars: hotel.stars }),
            ...(hotel.category_flags && { category_flags: hotel.category_flags }),
        },
    };
};

module.exports = {
    getDestinationObject,
    withHotelContext
};
